<template>
    <ChatAgentToolFrame class="w-full md:w-[530px] p-0 overflow-hidden" data-testid="learning-resource-widget">
        <a
            v-if="resource"
            :href="resource.url ?? '#'"
            :target="resource.url ? '_blank' : undefined"
            :rel="resource.url ? 'noopener noreferrer' : undefined"
            class="flex min-w-0 items-start gap-3 p-4 transition-colors"
            :class="resource.url ? 'hover:bg-valence-grey-50' : 'pointer-events-none'"
            :aria-disabled="!resource.url"
            data-testid="learning-resource-link">
            <div :class="contentTypeIconTileClass(resource.content_type)">
                <i :class="contentTypeIcon(resource.content_type)" aria-hidden="true"></i>
            </div>
            <div class="flex min-w-0 flex-1 flex-col gap-2">
                <div class="flex items-start justify-between gap-3">
                    <div class="flex min-w-0 flex-col gap-1">
                        <p class="details font-medium uppercase tracking-normal text-valence-primary-500">Learning resource</p>
                        <h3 class="body-small-semibold min-w-0 text-valence-grey-900">{{ resource.title }}</h3>
                    </div>
                    <i v-if="resource.url" class="bi bi-box-arrow-up-right mt-0.5 shrink-0 text-sm text-valence-grey-400" aria-hidden="true"></i>
                </div>

                <p v-if="resource.summary" class="details text-valence-grey-600 line-clamp-3">{{ resource.summary }}</p>

                <div class="details flex flex-wrap items-center gap-2 pt-1 text-valence-grey-500">
                    <span v-if="resource.content_type" :class="contentTypeChipClass(resource.content_type)">
                        {{ resource.content_type }}
                    </span>
                    <span
                        v-if="resource.duration_minutes"
                        class="inline-flex items-center gap-1 rounded-full border border-valence-grey-200 bg-white px-2.5 py-1 text-valence-grey-700">
                        <i class="bi bi-clock text-valence-primary-500" aria-hidden="true"></i>
                        {{ resource.duration_minutes }} min
                    </span>
                    <span v-if="resource.provider" class="truncate text-valence-grey-500">{{ resource.provider }}</span>
                </div>
            </div>
        </a>

        <div v-else class="flex items-start gap-3 p-4" data-testid="learning-resource-empty">
            <div class="size-9 shrink-0 rounded-lg bg-valence-grey-100 text-valence-grey-700 flex items-center justify-center">
                <i class="bi bi-search" aria-hidden="true"></i>
            </div>
            <div class="flex min-w-0 flex-col gap-1">
                <p class="body-small-semibold text-valence-grey-900">No matching resource found</p>
                <p class="details text-valence-grey-600">{{ action.action_params?.message ?? "Try a different topic or phrasing." }}</p>
            </div>
        </div>
    </ChatAgentToolFrame>
</template>

<script setup>
import { computed } from "vue";

import ChatAgentToolFrame from "./ChatAgentToolFrame.vue";

const props = defineProps({
    action: {
        type: Object,
        default: () => ({}),
    },
});

const resource = computed(() => props.action.action_params?.resource ?? null);

const contentTypeIcons = {
    article: "bi bi-file-text",
    assessment: "bi bi-ui-checks",
    book: "bi bi-book",
    course: "bi bi-mortarboard",
    event: "bi bi-calendar-event",
    podcast: "bi bi-mic",
    video: "bi bi-play-btn",
};

const fallbackContentTypeStyle = {
    chip: "border border-valence-grey-200 bg-valence-grey-100 text-valence-grey-700",
    iconTile: "bg-valence-grey-100 text-valence-grey-700",
};

const contentTypeStyle = {
    chip: "border border-valence-primary-200 bg-valence-primary-100 text-valence-primary-700",
    iconTile: "bg-valence-primary-100 text-valence-primary-600",
};

function normalizeContentType(contentType) {
    return contentType?.trim().toLowerCase() ?? "";
}

function contentTypeIcon(contentType) {
    const normalized = normalizeContentType(contentType);
    return contentTypeIcons[normalized] ?? "bi bi-collection-play";
}

function contentTypeChipClass(contentType) {
    const normalized = normalizeContentType(contentType);
    const colorClass = normalized ? contentTypeStyle.chip : fallbackContentTypeStyle.chip;
    return `inline-flex items-center rounded-full px-2.5 py-1 capitalize ${colorClass}`;
}

function contentTypeIconTileClass(contentType) {
    const normalized = normalizeContentType(contentType);
    const colorClass = normalized ? contentTypeStyle.iconTile : fallbackContentTypeStyle.iconTile;
    return `size-9 shrink-0 rounded-lg flex items-center justify-center ${colorClass}`;
}
</script>
